/**
 * Visão de "programa" da bonificação (lista e detalhe): uma bonificação por
 * ano da meta, com as parcelas (competências) fechando em sequência.
 */
import type { CorStatus } from "@/components/padroes";
import {
  descreverCalculo,
  type Bonificacao,
  type CompetenciaBonificacaoResumo,
  type StatusBonificacao,
} from "@/data/bonificacao";

export type StatusPrograma = "preparacao" | "em_pagamento" | "concluida";

export const ROTULO_STATUS_PROGRAMA: Record<StatusPrograma, { rotulo: string; cor: CorStatus }> = {
  preparacao: { rotulo: "Em preparação", cor: "cinza" },
  em_pagamento: { rotulo: "Em pagamento", cor: "ambar" },
  concluida: { rotulo: "Concluída", cor: "verde" },
};

type ParcelaMinima = { id: string; parcela: number; competencia: string; status: StatusBonificacao };

/** Nenhuma parcela fechada = em preparação; todas = concluída. */
export const statusPrograma = (competencias: { status: StatusBonificacao }[]): StatusPrograma => {
  const fechadas = competencias.filter((c) => c.status === "fechada").length;
  if (fechadas === 0) return "preparacao";
  return fechadas === competencias.length ? "concluida" : "em_pagamento";
};

/** A próxima parcela a fechar (as parcelas fecham em ordem). */
export const parcelaAtual = <T extends ParcelaMinima>(competencias: T[]): T | undefined =>
  [...competencias].sort((a, b) => a.parcela - b.parcela).find((c) => c.status === "rascunho");

/**
 * Estado de cada parcela no programa: fechada = paga; a próxima a fechar está
 * em rascunho; as seguintes ainda são previstas (valor pode mudar até lá).
 */
export type EstadoParcela = "paga" | "rascunho" | "prevista";

export const estadoParcela = (c: ParcelaMinima, atual: ParcelaMinima | undefined): EstadoParcela =>
  c.status === "fechada" ? "paga" : c.id === atual?.id ? "rascunho" : "prevista";

export const ESTILO_PARCELA: Record<EstadoParcela, { rotulo: string; barra: string; texto: string; ponto: string }> = {
  paga: { rotulo: "Fechada", barra: "bg-success", texto: "text-success", ponto: "bg-success" },
  rascunho: {
    rotulo: "Rascunho",
    barra: "bg-amber-500",
    texto: "text-amber-700 dark:text-amber-400",
    ponto: "bg-amber-500",
  },
  prevista: { rotulo: "Prevista", barra: "bg-muted", texto: "text-muted-foreground", ponto: "bg-muted-foreground/60" },
};

/** "Bonificação de Metas 2025" (ou a descrição dada pelo RH). */
export const tituloBonificacao = (
  b: Pick<Bonificacao, "descricao" | "dataBase" | "adiantamento">,
  nome: string,
) => b.descricao || `${nome} ${b.dataBase.slice(0, 4)}${b.adiantamento ? " · adiantamento" : ""}`;

/** "2026-07" -> "jul/26". */
export const mesCurto = (aaaamm: string) => {
  const [a, m] = aaaamm.split("-").map(Number);
  if (!a || !m) return aaaamm;
  const mes = new Date(a, m - 1, 1).toLocaleDateString("pt-BR", { month: "short" }).replace(".", "");
  return `${mes}/${String(a).slice(2)}`;
};

/** "outubro de 2026" -> "Outubro de 2026". */
export const capitalizar = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** "2x o salário fixo · 4 parcelas". */
export const regraCurta = (b: Pick<Bonificacao, "tipoCalculo" | "multiplicador" | "valorFixo" | "numeroParcelas">) =>
  `${descreverCalculo(b)} · ${b.numeroParcelas} ${b.numeroParcelas === 1 ? "parcela" : "parcelas"}`;

/**
 * Totais do programa a partir das parcelas: o total soma todas (as abertas são
 * estimativa até fechar), o pago só as fechadas.
 */
export const resumirPrograma = (competencias: CompetenciaBonificacaoResumo[]) => {
  const fechadas = competencias.filter((c) => c.status === "fechada");
  return {
    total: competencias.reduce((s, c) => s + c.totalLiquido, 0),
    pago: fechadas.reduce((s, c) => s + c.totalLiquido, 0),
    colaboradores: Math.max(0, ...competencias.map((c) => c.totalColaboradores)),
    concluidaEm: fechadas.reduce<string | null>((m, c) => (c.fechadaEm && (!m || c.fechadaEm > m) ? c.fechadaEm : m), null),
  };
};
